import { useEffect, useMemo, useState } from "react";
import { Download, FileText, ExternalLink, Megaphone, Link2 } from "lucide-react";
import api, { API_BASE } from "../../api/client";
import { formatPrice } from "../../lib/format";

/**
 * Minute-by-minute P&L timeline for a single swap.
 *
 * Shows BOTH counterparties (BULL and BEAR) side-by-side so each trader can
 * see how their own and the opposite side's margin / PnL / threshold evolved
 * minute by minute.
 *
 * Works for ACTIVE swaps (polled every 5s) and SETTLED swaps (loaded once).
 * Provides CSV + PDF exports of the full history. Admin-managed promo cards
 * are interleaved into the timeline every ~10 rows.
 */
export default function SnapshotTimeline({ swapId, coin, settled = false, userSide = "BULL" }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ads, setAds] = useState([]);
    const [downloading, setDownloading] = useState(null);
    const [attestation, setAttestation] = useState(null);
    const [chainEvents, setChainEvents] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = () => api.get(`/swaps/${swapId}/attestations`)
            .then((r) => { if (mounted) setChainEvents(r.data || []); })
            .catch(() => {});
        load();
        if (!settled) {
            const id = setInterval(load, 10000);
            return () => { mounted = false; clearInterval(id); };
        }
        return () => { mounted = false; };
    }, [swapId, settled]);

    useEffect(() => {
        if (!settled) return;
        let mounted = true;
        api.get(`/settlements/${swapId}/attestation`)
            .then((r) => { if (mounted) setAttestation(r.data); })
            .catch(() => {});
        return () => { mounted = false; };
    }, [swapId, settled]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const r = await api.get(`/swaps/${swapId}/snapshots`);
                if (mounted) { setRows(r.data || []); setLoading(false); }
            } catch { if (mounted) setLoading(false); }
        };
        load();
        if (!settled) {
            const id = setInterval(load, 5000);
            return () => { mounted = false; clearInterval(id); };
        }
        return () => { mounted = false; };
    }, [swapId, settled]);

    useEffect(() => {
        let mounted = true;
        api.get("/feed/ads?placement=timeline")
            .then((r) => { if (mounted) setAds(r.data || []); })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    const reversed = useMemo(() => rows.slice().reverse(), [rows]);

    const handleExport = async (fmt) => {
        setDownloading(fmt);
        try {
            const token = localStorage.getItem("verslan_token");
            const url = `${API_BASE}/swaps/${swapId}/snapshots.${fmt}`;
            const res = await fetch(url, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: "include",
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = `verslan-bube-${coin || "swap"}-${swapId.slice(0, 8)}.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (e) {
            console.error("export failed", e);
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div
            className="border-t border-[#1F1F24]/60 px-3 py-3 bg-black/30"
            data-testid={`snapshot-timeline-${swapId}`}
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">
                        Minute-by-minute timeline · both counterparties
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                        {rows.length} snapshots · your side <span className="text-zinc-300">{userSide}</span>
                        {settled && <span className="ml-2 text-zinc-500">· settled (read-only)</span>}
                    </div>
                    {chainEvents.length > 0 && (
                        <details className="mt-1.5 group" data-testid={`chain-events-${swapId}`}>
                            <summary className="cursor-pointer inline-flex items-center gap-1.5 text-[10px] font-mono text-cyan-400/90 hover:text-cyan-300 border border-cyan-500/30 hover:border-cyan-400/60 px-2 py-1 rounded-sm">
                                <Link2 className="w-3 h-3" />
                                {chainEvents.length} on-chain event{chainEvents.length === 1 ? "" : "s"} on Hyperliquid testnet
                            </summary>
                            <div className="mt-2 grid gap-1 pl-1">
                                {chainEvents.map((ev) => (
                                    <a
                                        key={ev.id}
                                        href={ev.explorer_url || "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`chain-event-${ev.id}`}
                                        className="flex items-center gap-2 text-[10px] font-mono text-zinc-400 hover:text-cyan-300"
                                    >
                                        <span className={`px-1.5 py-0.5 rounded-sm text-[9px] tracking-[0.18em] uppercase ${ev.tx_hash ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-amber-500/10 text-amber-400 border border-amber-500/30"}`}>
                                            {ev.event_type.replace("_", " ")}
                                        </span>
                                        <span className="text-zinc-500">{new Date(ev.attempted_at).toLocaleString([], { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                                        {ev.tx_hash ? (
                                            <>
                                                <span className="truncate max-w-[180px] sm:max-w-[260px]">{ev.tx_hash}</span>
                                                <ExternalLink className="w-3 h-3 shrink-0" />
                                            </>
                                        ) : (
                                            <span className="text-amber-400/80 truncate">{ev.error || "pending"}</span>
                                        )}
                                    </a>
                                ))}
                            </div>
                        </details>
                    )}
                    {settled && attestation && (
                        <div className="mt-1.5">
                            {attestation.tx_hash ? (
                                <a
                                    href={attestation.explorer_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`attestation-link-${swapId}`}
                                    className="inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-400/60 px-2 py-1 rounded-sm"
                                    title="On-chain attestation on Hyperliquid testnet"
                                >
                                    <Link2 className="w-3 h-3" />
                                    <span className="hidden sm:inline">HL testnet ·</span>
                                    <span className="truncate max-w-[160px]">{attestation.tx_hash}</span>
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            ) : (
                                <span
                                    data-testid={`attestation-pending-${swapId}`}
                                    className="inline-flex items-center gap-1.5 text-[10px] font-mono text-amber-400/80 border border-amber-500/30 px-2 py-1 rounded-sm"
                                    title={attestation.error || "pending"}
                                >
                                    <Link2 className="w-3 h-3" />
                                    HL attestation pending
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid={`export-csv-${swapId}`}
                        onClick={() => handleExport("csv")}
                        disabled={downloading === "csv" || rows.length === 0}
                        className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-2.5 py-1 rounded-sm disabled:opacity-40"
                    >
                        <Download className="w-3 h-3" />
                        {downloading === "csv" ? "…" : "CSV"}
                    </button>
                    <button
                        data-testid={`export-pdf-${swapId}`}
                        onClick={() => handleExport("pdf")}
                        disabled={downloading === "pdf" || rows.length === 0}
                        className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-2.5 py-1 rounded-sm disabled:opacity-40"
                    >
                        <FileText className="w-3 h-3" />
                        {downloading === "pdf" ? "…" : "PDF"}
                    </button>
                </div>
            </div>

            {loading && (
                <div className="py-4 text-center text-[10px] font-mono text-zinc-600">loading…</div>
            )}
            {!loading && rows.length === 0 && (
                <div className="py-4 text-center text-[10px] font-mono text-zinc-600">
                    — no snapshots captured —
                </div>
            )}
            {rows.length > 0 && (
                <div className="mt-2 max-h-[420px] overflow-y-auto overflow-x-auto">
                    <table className="w-full min-w-[760px]" data-testid={`snapshot-table-${swapId}`}>
                        <thead className="sticky top-0 bg-black/90 backdrop-blur z-10">
                            <tr className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500 border-b border-[#1F1F24]">
                                <th className="text-left py-2 px-2 font-normal">Date / Time</th>
                                <th className="text-right py-2 px-2 font-normal">Price</th>
                                <th className="text-right py-2 px-2 font-normal">Vol%</th>
                                <th className="text-right py-2 px-2 font-normal text-emerald-400/80" colSpan={3}>Bull side</th>
                                <th className="text-right py-2 px-2 font-normal text-rose-400/80" colSpan={3}>Bear side</th>
                            </tr>
                            <tr className="text-[8.5px] font-mono uppercase tracking-[0.2em] text-zinc-600 border-b border-[#1F1F24]/70">
                                <th colSpan={3}></th>
                                <th className="text-right py-1 px-2 font-normal">Margin</th>
                                <th className="text-right py-1 px-2 font-normal">PnL</th>
                                <th className="text-right py-1 px-2 font-normal">Thr%</th>
                                <th className="text-right py-1 px-2 font-normal">Margin</th>
                                <th className="text-right py-1 px-2 font-normal">PnL</th>
                                <th className="text-right py-1 px-2 font-normal">Thr%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reversed.map((r, i) => {
                                const vol = r.volatility_pct ?? 0;
                                const volColor = vol > 0 ? "text-emerald-400" : vol < 0 ? "text-rose-400" : "text-zinc-400";
                                const bullPos = (r.bull_pnl ?? 0) >= 0;
                                const bearPos = (r.bear_pnl ?? 0) >= 0;
                                const isUserBull = userSide === "BULL";
                                const ad = ads.length > 0 && i > 0 && i % 10 === 0 ? ads[Math.floor(i / 10 - 1) % ads.length] : null;
                                return [
                                    <tr
                                        key={i}
                                        data-testid={`snapshot-row-${swapId}-${i}`}
                                        className="border-t border-[#1F1F24]/40 font-mono text-[11px]"
                                    >
                                        <td className="py-1.5 px-2 whitespace-nowrap">
                                            <div className="text-zinc-100">
                                                {new Date(r.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                            </div>
                                            <div className="text-[9px] text-zinc-500">
                                                {new Date(r.ts).toLocaleDateString([], { year: "numeric", month: "short", day: "2-digit" })}
                                            </div>
                                        </td>
                                        <td className="py-1.5 px-2 text-right text-zinc-100">{formatPrice(r.current_price)}</td>
                                        <td className={`py-1.5 px-2 text-right ${volColor}`}>
                                            {vol === 0 ? "0%" : `${vol > 0 ? "+" : ""}${vol.toFixed(4)}%`}
                                        </td>
                                        <td className={`py-1.5 px-2 text-right ${isUserBull ? "text-zinc-100" : "text-zinc-300"}`}>
                                            {(r.margin_bull ?? 0).toFixed(2)}
                                        </td>
                                        <td className={`py-1.5 px-2 text-right ${bullPos ? "text-emerald-400" : "text-rose-400"}`}>
                                            {bullPos ? "+" : ""}{(r.bull_pnl ?? 0).toFixed(2)}
                                        </td>
                                        <td className="py-1.5 px-2 text-right text-zinc-400">
                                            {(r.threshold_bull_pct ?? 0).toFixed(1)}%
                                        </td>
                                        <td className={`py-1.5 px-2 text-right ${!isUserBull ? "text-zinc-100" : "text-zinc-300"}`}>
                                            {(r.margin_bear ?? 0).toFixed(2)}
                                        </td>
                                        <td className={`py-1.5 px-2 text-right ${bearPos ? "text-emerald-400" : "text-rose-400"}`}>
                                            {bearPos ? "+" : ""}{(r.bear_pnl ?? 0).toFixed(2)}
                                        </td>
                                        <td className="py-1.5 px-2 text-right text-zinc-400">
                                            {(r.threshold_bear_pct ?? 0).toFixed(1)}%
                                        </td>
                                    </tr>,
                                    ad && (
                                        <AdRow
                                            key={`ad-${i}-${ad.id}`}
                                            ad={ad}
                                            onImpression={() => api.post(`/feed/ads/${ad.id}/impression`).catch(() => {})}
                                        />
                                    ),
                                ];
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function AdRow({ ad, onImpression }) {
    useEffect(() => {
        onImpression?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <tr data-testid={`ad-row-${ad.id}`} className="border-t border-cyan-500/20 bg-cyan-500/[0.04]">
            <td colSpan={9} className="px-3 py-3">
                <div className="flex items-start gap-3">
                    {ad.image_url ? (
                        <img
                            src={ad.image_url}
                            alt=""
                            className="w-12 h-12 rounded-sm object-cover border border-[#1F1F24]"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                    ) : (
                        <div className="w-12 h-12 rounded-sm bg-cyan-500/10 border border-cyan-500/30 grid place-items-center shrink-0">
                            <Megaphone className="w-5 h-5 text-cyan-400" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-cyan-400/80">Sponsored · Verslan</div>
                        <div className="text-sm font-medium text-zinc-100 mt-0.5 truncate">{ad.headline}</div>
                        {ad.body && <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{ad.body}</div>}
                    </div>
                    {ad.cta_url && (
                        <a
                            href={ad.cta_url}
                            target={ad.cta_url.startsWith("http") ? "_blank" : "_self"}
                            rel="noopener noreferrer"
                            data-testid={`ad-cta-${ad.id}`}
                            onClick={() => api.post(`/feed/ads/${ad.id}/click`).catch(() => {})}
                            className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-black bg-cyan-400 hover:bg-cyan-300 px-3 py-1.5 rounded-sm font-semibold"
                        >
                            {ad.cta_label || "Learn more"}
                            <ExternalLink className="w-3 h-3" />
                        </a>
                    )}
                </div>
            </td>
        </tr>
    );
}
